import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { itemVariants } from '../../lib/animations';
import RecommendationCard from './RecommendationCard';
import {
  CAROUSEL_COLORS,
  CAROUSEL_TYPOGRAPHY,
  CAROUSEL_DIMENSIONS,
  CAROUSEL_PADDING,
  SECTION_TITLE_CLASSES,
} from './constants';
import type { RecommendedActivity } from '../../types/recommendation.types';

interface RecommendationsCarouselProps {
  activities: RecommendedActivity[];
  isRegenerating?: boolean;
  childName?: string;
}

/**
 * RecommendationsCarousel Component
 *
 * Horizontal scrolling carousel for category recommendations.
 * Design spec: Figma node 2213:11743 (Recommendations Container)
 *
 * Features:
 * - Section title: "Activities that improve this skill"
 * - Horizontal scroll with hidden scrollbar for clean UI
 * - Regeneration indicator when new recommendations are being generated
 * - Hidden entirely when activities array is empty
 * - Responsive padding and card sizing
 */
export default function RecommendationsCarousel({
  activities,
  isRegenerating = false,
  childName,
}: RecommendationsCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartLeft = useRef(0);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartLeft.current = el.scrollLeft;
    el.setPointerCapture(e.pointerId);
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dx = e.clientX - dragStartX.current;
    scrollRef.current.scrollLeft = scrollStartLeft.current - dx;
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const el = scrollRef.current;
    if (el) {
      el.releasePointerCapture(e.pointerId);
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    }
  }, []);

  // Hide section if no activities
  if (activities.length === 0) {
    return null;
  }

  return (
    <motion.div
      variants={itemVariants}
      className="w-full"
    >
      {/* Section Header */}
      <div className={CAROUSEL_PADDING}>
        <h2
          className={SECTION_TITLE_CLASSES}
          style={{
            ...CAROUSEL_TYPOGRAPHY.sectionTitle,
            color: CAROUSEL_COLORS.titleText,
            margin: 0,
            marginBottom: `${CAROUSEL_DIMENSIONS.sectionGap}px`,
            fontVariationSettings: "'opsz' 14",
          }}
        >
          Activities that improve this skill
        </h2>

        {/* Regeneration Indicator */}
        {isRegenerating && (
          <p
            style={{
              ...CAROUSEL_TYPOGRAPHY.metadata,
              color: CAROUSEL_COLORS.skillTextPrimary,
              margin: 0,
              marginTop: '8px',
              fontVariationSettings: "'opsz' 14",
            }}
          >
            Finding fresh activities{childName ? ` for ${childName}` : ''}...
          </p>
        )}
      </div>

      {/* Carousel Container */}
      <div
        ref={scrollRef}
        className={`overflow-x-auto ${CAROUSEL_PADDING}`}
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
          cursor: 'grab',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {/* Cards Row */}
        <div
          className="flex pb-4"
          style={{
            gap: `${CAROUSEL_DIMENSIONS.carouselGap}px`,
          }}
        >
          {activities.map((activity) => (
            <RecommendationCard
              key={activity.id}
              activity={activity}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}
